import React, {useEffect, useState} from 'react';
import {
    flexRender,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getExpandedRowModel,
    SortingState,
} from '@tanstack/react-table';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {Tooltip} from '@/components/tooltips';


const TableComponent = ({
    data,
    columns,
    state = {},
    onExpandedChange = () => { },
    renderSubComponent = null,
    customTable = false,
    sortable = false,
    tableClassName = '',
}: {
    data: any[];
    columns: any[];
    state?: any;
    onExpandedChange?: () => void;
    renderSubComponent?: any;
    customTable?: boolean;
    sortable?: boolean;
    tableClassName?: string;
}) => {

    const [sorting, setSorting] = useState<SortingState>([])

    const safeData = Array.isArray(data) ? data : [];

    const table = useReactTable({
        data: safeData,
        columns,
        getRowCanExpand: () => true,
        // Merge internal sorting state with any externally-controlled state
        state: {
            sorting,
            ...state,
        },
        onExpandedChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onSortingChange: setSorting,
    });

    const noDataIndication = i18n.t('jt_no_data_available.text');
    const [largeScreen, setLargeScreen] = useState(window.innerWidth >= 576);

    useEffect(() => {
        const handleResize = () => {
            setLargeScreen(window.innerWidth >= 576);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <>

            <div className="table-responsive"
                style={{
                    overflow: largeScreen ? 'visible' : 'scroll',
                }}
            >
                <table className={`table table-hover table-bordered g-table w-100${tableClassName ? ` ${tableClassName}` : ''}`}>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup: any) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header: any, index: number) => (
                                    <th key={header.id}
                                        style={{
                                            width: (index === headerGroup.headers.length - 1 && !customTable) ? 120 : 'auto',
                                        }}
                                        className={(index === headerGroup.headers.length - 1 && headerGroup.headers.length > 1 && !customTable) ? 'text-right' : ''}>
                                        {header.isPlaceholder
                                            ? null
                                            : sortable ? (
                                                <div
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    title={
                                                        header.column.getCanSort()
                                                            ? header.column.getNextSortingOrder() === 'asc'
                                                                ? i18n.t('sort_asc.text')
                                                                : header.column.getNextSortingOrder() === 'desc'
                                                                    ? i18n.t('sort_desc.text')
                                                                    : i18n.t('sort_cl.text')
                                                            : undefined
                                                    }
                                                    style={{
                                                        cursor: `${header.column.getCanSort()
                                                            ? 'pointer'
                                                            : 'default'}`
                                                    }}
                                                >
                                                    <span>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {header.column.getCanSort()
                                                            ? <Icon name="mdi-sort" size="xs" className="ml-1" />
                                                            : null
                                                        }
                                                    </span>
                                                </div>
                                            ) : (flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            ))}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    {table.getRowModel().rows.length === 0 && (
                        <tbody>
                            <tr>
                                <td colSpan={columns.length}>
                                    {noDataIndication}
                                </td>
                            </tr>
                        </tbody>
                    )}
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <React.Fragment key={row.id}>
                                    <tr>
                                        {row.getVisibleCells().map((cell, index) => (
                                            <td key={cell.id}
                                                className={(index === row.getVisibleCells().length - 1 && row.getVisibleCells().length > 1 && !customTable) ? 'text-right' : ''}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                    {row.getIsExpanded() && (
                                        <tr>
                                            <td colSpan={row.getVisibleCells().length}>
                                                {/* @ts-ignore */}
                                                {renderSubComponent({row})}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    )
    ;
};

export default TableComponent;
